import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '../../utils';
import { useEditorStore } from '../../store';
import { BUILT_IN_TOOLS, TOOL_CATEGORIES } from '../tools/registry';
import type { ToolDefinition, ToolCategory } from '../../types/editor.types';

const CAT: Record<string, { icon: string; dot: string; label: string }> = {
  content: { icon: 'bg-violet-100 dark:bg-violet-900/25 text-violet-600 dark:text-violet-400', dot: 'bg-violet-500', label: 'text-violet-600 dark:text-violet-400' },
  layout: { icon: 'bg-sky-100 dark:bg-sky-900/25 text-sky-600 dark:text-sky-400', dot: 'bg-sky-500', label: 'text-sky-600 dark:text-sky-400' },
  media: { icon: 'bg-rose-100 dark:bg-rose-900/25 text-rose-600 dark:text-rose-400', dot: 'bg-rose-500', label: 'text-rose-600 dark:text-rose-400' },
  interactive: { icon: 'bg-amber-100 dark:bg-amber-900/25 text-amber-600 dark:text-amber-400', dot: 'bg-amber-500', label: 'text-amber-600 dark:text-amber-400' },
  social: { icon: 'bg-emerald-100 dark:bg-emerald-900/25 text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'text-emerald-600 dark:text-emerald-400' },
  advanced: { icon: 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400', dot: 'bg-slate-400', label: 'text-slate-500 dark:text-slate-400' },
};

const DRAG_THRESHOLD = 4;

function useChipsDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startLeft = useRef(0);

  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (delta === 0) return;
    e.preventDefault();
    el.scrollLeft += delta;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button') === null) return;

    const el = ref.current;
    if (!el) return;

    isDragging.current = false;
    startX.current = e.clientX;
    startLeft.current = el.scrollLeft;

    el.setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
  }, []);

  /* Pointer-move: perform drag scroll */
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !el.hasPointerCapture(e.pointerId)) return;

    const moved = e.clientX - startX.current;
    if (!isDragging.current && Math.abs(moved) > DRAG_THRESHOLD) {
      isDragging.current = true;
    }
    if (isDragging.current) {
      el.scrollLeft = startLeft.current - moved;
    }
  }, []);

  /* Pointer-up: end drag, restore cursor */
  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    el.style.cursor = '';
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (isDragging.current) {
      e.stopPropagation();
      isDragging.current = false;
    }
  }, []);

  return { ref, onWheel, onPointerDown, onPointerMove, onPointerUp, onClickCapture };
}

/* ══ DRAGGABLE TILE ════════════════════════════════════════ */
function ToolTile({ tool }: { tool: ToolDefinition }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tool:${tool.id}`,
    data: { type: 'tool', toolId: tool.id, tool },
  });

  const Icon = (LucideIcons as unknown as Record<string, LucideIcon>)[tool.icon];
  const palette = CAT[tool.category] ?? CAT.content;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={`Drag to add ${tool.label}`}
      className={cn(
        'group relative flex flex-col items-center gap-2 pt-3.5 pb-2.5 px-1.5 rounded-xl',
        'cursor-grab select-none',
        'bg-surface dark:bg-dark-700/50',
        'border border-line-subtle dark:border-dark-600',
        'shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)]',
        'hover:border-violet-200 dark:hover:border-violet-500/60',
        'hover:shadow-[0_4px_12px_rgba(124,58,237,0.10)] dark:hover:shadow-[0_4px_16px_rgba(139,92,246,0.18)]',
        'hover:-translate-y-0.5 transition-all duration-150',
        isDragging && 'opacity-30 scale-95 cursor-grabbing shadow-none',
      )}
    >
      <div className={cn(
        'w-9 h-9 rounded-xl flex items-center justify-center',
        'transition-transform duration-150 group-hover:scale-110',
        palette.icon,
      )}>
        {Icon && <Icon className="size-[17px]" strokeWidth={1.75} />}
      </div>

      <span className={cn(
        'text-micro font-semibold text-center leading-tight w-full truncate',
        'text-content-muted dark:text-dark-400',
        'group-hover:text-gray-800 dark:group-hover:text-dark-200',
        'transition-colors duration-150',
      )}>
        {tool.label}
      </span>

      {isDragging && (
        <div className="absolute inset-0 rounded-xl ring-2 ring-violet-400 ring-offset-1" />
      )}
    </div>
  );
}

/* ══ MAIN PANEL ════════════════════════════════════════════ */
export function ToolsPanel() {
  const { config } = useEditorStore();
  const [activeCategory, setActiveCategory] = useState<ToolCategory | 'all'>('all');
  const [search, setSearch] = useState('');

  /* Overflow indicators — show fades when content is clipped */
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const chips = useChipsDragScroll();

  /* Update fade indicators on scroll / resize */
  const updateFades = useCallback(() => {
    const el = chips.ref.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, [chips.ref]);

  const scrollChipIntoView = useCallback((target: HTMLElement | null) => {
    const el = chips.ref.current;
    if (!el || !target) return;
    const pad = 10;
    const cr = el.getBoundingClientRect();
    const tr = target.getBoundingClientRect();
    // Already fully visible — nothing to do
    if (tr.left >= cr.left + pad && tr.right <= cr.right - pad) return;
    let delta = 0;
    if (tr.left < cr.left + pad) {
      delta = tr.left - (cr.left + pad);
    } else if (tr.right > cr.right - pad) {
      delta = tr.right - (cr.right - pad);
    }
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollTo({ left: Math.min(Math.max(0, el.scrollLeft + delta), maxScroll), behavior: 'smooth' });
  }, [chips.ref]);

  /* Step-scroll for arrow buttons */
  const scrollBy = useCallback((dir: 'left' | 'right') => {
    const el = chips.ref.current;
    if (!el) return;
    const step = Math.round(el.clientWidth * 0.6);
    el.scrollBy({ left: dir === 'right' ? step : -step, behavior: 'smooth' });
  }, [chips.ref]);

  useEffect(() => {
    const el = chips.ref.current;
    if (!el) return;
    updateFades();
    el.addEventListener('scroll', updateFades, { passive: true });
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateFades); ro.disconnect(); };
  }, [chips.ref, updateFades]);

  useEffect(() => {
    const el = chips.ref.current;
    if (!el) return;
    const activeBtn = el.querySelector<HTMLElement>('[data-active="true"]');
    if (activeBtn) scrollChipIntoView(activeBtn);
  }, [activeCategory, scrollChipIntoView]);

  const allChips = [{ id: 'all' as const, label: 'All' }, ...TOOL_CATEGORIES];

  const filtered = BUILT_IN_TOOLS.filter((t) => {
    const matchesMode = t.supportedModes.includes(config.mode);
    const matchesCategory = activeCategory === 'all' || t.category === activeCategory;
    const matchesSearch = t.label.toLowerCase().includes(search.toLowerCase());
    return matchesMode && matchesCategory && matchesSearch;
  });

  const grouped = TOOL_CATEGORIES.filter((cat) =>
    filtered.some((t) => t.category === cat.id)
  );

  return (
    <div className="flex flex-col h-full bg-gray-50/50 dark:bg-dark-800">

      {/* ── Search ── */}
      <div className="px-3 pt-2.5 pb-2">
        <div className="relative group">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5
            text-content-subtle dark:text-dark-500 pointer-events-none
            group-focus-within:text-violet-500 dark:group-focus-within:text-violet-400
            transition-colors duration-150" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search elements…"
            className="w-full h-8 pl-8 pr-7 text-[12px] rounded-lg
              bg-surface dark:bg-dark-800
              border border-line dark:border-dark-600/70
              text-gray-800 dark:text-dark-100
              placeholder-gray-400 dark:placeholder-dark-500
              shadow-[0_1px_2px_rgba(0,0,0,0.04)]
              focus:outline-none focus:ring-2 focus:ring-violet-400/25
              focus:border-violet-400 dark:focus:border-violet-500/60
              transition-all duration-150"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2
                w-4 h-4 rounded-full flex items-center justify-center
                bg-gray-200 dark:bg-dark-600 text-content-subtle dark:text-dark-400
                hover:bg-violet-100 hover:text-violet-600
                dark:hover:bg-violet-900/30 dark:hover:text-violet-400
                transition-all duration-150"
            >
              <X className="size-2.5" />
            </button>
          )}
        </div>
      </div>

      <div className="relative px-3 pb-2.5 group/strip">

        {/* ── Left arrow button ── */}
        <button
          aria-label="Scroll categories left"
          onClick={() => scrollBy('left')}
          className={cn(
            'absolute left-1 top-1/2 -translate-y-1/2 z-20',
            'w-5 h-5 rounded-full flex items-center justify-center',
            'bg-surface dark:bg-dark-700 border border-line dark:border-dark-600',
            'shadow-sm text-content-subtle dark:text-dark-400',
            'hover:text-violet-600 dark:hover:text-violet-400 hover:border-violet-300 dark:hover:border-violet-500/60',
            'transition-all duration-150',
            'translate-y-[calc(-50%-5px)]',
            showLeftFade ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          )}
        >
          <svg className="size-2.5" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 1L2 4l3 3" />
          </svg>
        </button>

        {/* Left overflow fade */}
        <div className={cn(
          'absolute left-3 top-0 bottom-2.5 w-8 pointer-events-none z-10',
          'bg-gradient-to-r from-gray-50 dark:from-dark-800 to-transparent',
          'transition-opacity duration-200',
          showLeftFade ? 'opacity-100' : 'opacity-0',
        )} />

        <div
          ref={chips.ref}
          role="tablist"
          aria-label="Element categories"
          className="flex gap-1.5 overflow-x-auto no-scrollbar select-none"
          onWheel={chips.onWheel}
          onPointerDown={chips.onPointerDown}
          onPointerMove={chips.onPointerMove}
          onPointerUp={chips.onPointerUp}
          onClickCapture={chips.onClickCapture}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            const btns = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button'));
            const idx = btns.indexOf(document.activeElement as HTMLElement);
            if (idx === -1) return;
            const next = e.key === 'ArrowRight'
              ? btns[Math.min(idx + 1, btns.length - 1)]
              : btns[Math.max(idx - 1, 0)];
            next?.focus();
            scrollChipIntoView(next);
          }}
        >
          {allChips.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                role="tab"
                aria-selected={isActive}
                data-active={isActive ? 'true' : undefined}
                tabIndex={isActive ? 0 : -1}
                onClick={(e) => {
                  setActiveCategory(cat.id as ToolCategory | 'all');
                  scrollChipIntoView(e.currentTarget);
                }}
                className={cn(
                  'shrink-0 px-3 py-1 rounded-full text-micro font-semibold whitespace-nowrap',
                  'transition-all duration-150 select-none',
                  isActive
                    ? 'bg-violet-600 text-white shadow-sm shadow-violet-500/20'
                    : [
                      'bg-surface dark:bg-dark-800',
                      'border border-line dark:border-dark-600/70',
                      'text-content-muted dark:text-dark-400',
                      'hover:border-violet-300 dark:hover:border-violet-600/50',
                      'hover:text-violet-600 dark:hover:text-violet-400',
                      'shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
                    ],
                )}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        <div className={cn(
          'absolute right-3 top-0 bottom-2.5 w-8 pointer-events-none z-10',
          'bg-gradient-to-l from-gray-50 dark:from-dark-800 to-transparent',
          'transition-opacity duration-200',
          showRightFade ? 'opacity-100' : 'opacity-0',
        )} />

        {/* ── Right arrow button ── */}
        <button
          aria-label="Scroll categories right"
          onClick={() => scrollBy('right')}
          className={cn(
            'absolute right-1 top-1/2 z-20',
            'w-5 h-5 rounded-full flex items-center justify-center',
            'bg-surface dark:bg-dark-700 border border-line dark:border-dark-600',
            'shadow-sm text-content-subtle dark:text-dark-400',
            'hover:text-violet-600 dark:hover:text-violet-400 hover:border-violet-300 dark:hover:border-violet-500/60',
            'transition-all duration-150',
            'translate-y-[calc(-50%-5px)]',
            showRightFade ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
          )}
        >
          <svg className="size-2.5" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 1l3 3-3 3" />
          </svg>
        </button>
      </div>

      {/* ── Divider ── */}
      <div className="mx-3 mb-2 h-px bg-gray-100 dark:bg-dark-700/50" />

      {/* ── Element grid ── */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-4
        scrollbar-thin scrollbar-track-transparent
        scrollbar-thumb-gray-200 dark:scrollbar-thumb-dark-700">

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
              <Search className="size-4 text-gray-300 dark:text-dark-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-content-muted dark:text-dark-400">No elements</p>
              <p className="text-[11px] text-content-subtle dark:text-dark-500 mt-0.5">Try a different keyword</p>
            </div>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="text-[11px] font-bold text-violet-500 hover:text-violet-700
                  dark:text-violet-400 dark:hover:text-violet-300
                  underline underline-offset-2 transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : activeCategory === 'all' ? (
          grouped.map((cat) => {
            const catTools = filtered.filter((t) => t.category === cat.id);
            if (!catTools.length) return null;
            const palette = CAT[cat.id] ?? CAT.content;
            return (
              <div key={cat.id}>
                <div className="flex items-center gap-2 mb-2 px-0.5">
                  <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', palette.dot)} />
                  <span className={cn('text-[10px] font-extrabold uppercase tracking-[0.13em]', palette.label)}>
                    {cat.label}
                  </span>
                  <span className="ml-auto text-[10px] font-semibold text-gray-300 dark:text-dark-600 tabular-nums">
                    {catTools.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {catTools.map((tool) => <ToolTile key={tool.id} tool={tool} />)}
                </div>
              </div>
            );
          })
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {filtered.map((tool) => <ToolTile key={tool.id} tool={tool} />)}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-center gap-1.5 py-2 mx-3
        border-t border-line-subtle dark:border-dark-700/50">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 dark:bg-violet-500 animate-pulse" />
        <p className="text-[10px] text-content-subtle dark:text-dark-500 font-semibold tracking-wide">
          Drag onto canvas
        </p>
      </div>
    </div>
  );
}
